import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const CONFIRM_TEXT = 'RESTAURAR';

interface RestoreResult {
  preRestoreFile: string;
  appliedFrom: string;
}

/**
 * Pantalla /admin/copia-de-seguridad.
 *
 * Dos secciones independientes:
 *   1. Descargar copia → arranca download directo del endpoint
 *      (el navegador maneja el streaming; no buffereamos en JS).
 *   2. Restaurar copia → upload + doble confirmación (texto "RESTAURAR"
 *      + contraseña reintroducida). Tras éxito, la sesión actual queda
 *      inválida (la tabla session se sobreescribe con la del ZIP), así
 *      forzamos un reload a /login.
 */
export function AdminBackup() {
  return (
    <div className="p-6 md:p-8 max-w-3xl mx-auto">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight2">Copia de seguridad</h1>
        <p className="text-[14px] text-muted-foreground mt-1">
          Descarga un ZIP con la base de datos y los documentos subidos para guardar fuera del
          servidor, o restaura el sistema desde un ZIP descargado previamente.
        </p>
      </header>

      <div className="space-y-5">
        <DownloadSection />
        <RestoreSection />
      </div>
    </div>
  );
}

function DownloadSection() {
  return (
    <Panel
      title="Descargar copia de seguridad"
      description="Genera y descarga un ZIP con todos los datos de la empresa."
    >
      <div className="px-6 py-5 space-y-4">
        <div className="text-[14px] text-foreground space-y-2">
          <p>El ZIP contiene:</p>
          <ul className="list-disc list-inside text-muted-foreground space-y-0.5 pl-1">
            <li>
              <span className="text-foreground">Base de datos completa</span>: usuarios, fichajes,
              vacaciones, ausencias, ajustes, log de auditoría.
            </li>
            <li>
              <span className="text-foreground">Documentos</span>: justificantes de ausencia, fotos
              de tickets de viaje, nóminas subidas.
            </li>
            <li>
              <span className="text-foreground">Metadatos</span>: versión de la app, fecha y quién
              generó la copia.
            </li>
          </ul>
        </div>

        <div className="rounded-sm border border-warning/40 bg-warning/5 px-4 py-3">
          <p className="text-[13px] font-medium text-foreground">⚠ Información sensible.</p>
          <p className="text-[12px] text-muted-foreground mt-1 leading-relaxed">
            El archivo contiene datos personales de empleados (nombres, DNI, fichajes, documentos).
            Trátalo como confidencial: no lo envíes por email, no lo dejes en carpetas compartidas
            sin cifrar, y bórralo cuando ya no lo necesites.
          </p>
        </div>

        <div className="flex items-center gap-3 pt-1">
          <Button asChild variant="primary">
            <a href="/api/v1/admin/backup/download" download>
              Descargar copia de seguridad
            </a>
          </Button>
          <p className="text-[12px] text-muted-foreground">
            Empezará la descarga directamente; el archivo se genera al vuelo.
          </p>
        </div>
      </div>
    </Panel>
  );
}

function RestoreSection() {
  const [file, setFile] = useState<File | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RestoreResult | null>(null);

  const confirmOk = confirmText === CONFIRM_TEXT;
  const passwordOk = password.length > 0;
  const fileOk = !!file && file.name.toLowerCase().endsWith('.zip');
  const ready = confirmOk && passwordOk && fileOk;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!ready || !file) return;
    setSubmitting(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('confirmText', confirmText);
      fd.append('password', password);
      const res = await fetch('/api/v1/admin/backup/restore', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const payload = (await res.json()) as { message?: string | string[] };
          if (typeof payload.message === 'string') msg = payload.message;
          else if (Array.isArray(payload.message) && payload.message[0]) msg = payload.message[0];
        } catch {
          /* no JSON */
        }
        throw new Error(msg);
      }
      const data = (await res.json()) as RestoreResult;
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido conectar con el servidor.');
    } finally {
      setSubmitting(false);
    }
  }

  // Tras el restore exitoso, la sesión actual del HR_ADMIN ya no es válida
  // (la tabla session se sobreescribió). Forzamos redirect a login en 5s.
  if (result) {
    setTimeout(() => {
      window.location.href = '/login';
    }, 5000);
    return (
      <Panel title="Restauración completada" description="Vuelve a entrar para continuar.">
        <div className="px-6 py-5 space-y-3">
          <p className="text-[14px] text-foreground">
            Los datos se han restaurado desde la copia (exportada {result.appliedFrom}). Tu sesión
            actual ya no es válida — se redirigirá al login en unos segundos.
          </p>
          <p className="text-[12px] text-muted-foreground">
            Copia de seguridad pre-restore guardada en{' '}
            <code className="bg-muted px-1 py-0.5 rounded text-[11px]">{result.preRestoreFile}</code>
            . Si algo no es lo esperado tras volver a entrar, contacta con el responsable técnico
            para revertir.
          </p>
          <Button onClick={() => (window.location.href = '/login')} variant="primary" size="sm">
            Ir al login ahora
          </Button>
        </div>
      </Panel>
    );
  }

  return (
    <Panel
      title="Restaurar copia de seguridad"
      description="Sustituye la BD y los documentos por los de un ZIP previamente descargado."
    >
      <form onSubmit={onSubmit} className="px-6 py-5 space-y-5">
        <div className="rounded-sm border-2 border-destructive/60 bg-destructive/5 px-4 py-3">
          <p className="text-[13px] font-bold text-destructive">⚠ Operación destructiva.</p>
          <p className="text-[12px] text-foreground/80 mt-1 leading-relaxed">
            Esta acción <strong>borra todos los datos actuales</strong> (fichajes, vacaciones,
            documentos, log de auditoría…) y los reemplaza por los del ZIP que subas. <strong>No
            es reversible</strong> desde el panel — la única forma de volver atrás sería pedir al
            responsable técnico que restaure el pre-restore backup que el sistema crea
            automáticamente antes de aplicar tu ZIP.
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="zip-file">Archivo ZIP de copia de seguridad</Label>
          <Input
            id="zip-file"
            type="file"
            accept=".zip,application/zip"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            disabled={submitting}
          />
          {file ? (
            <p className="text-[12px] text-muted-foreground">
              Seleccionado: <span className="font-mono">{file.name}</span> (
              {(file.size / 1024).toFixed(1)} KB)
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="confirm-text">
            Para confirmar, escribe exactamente <code>{CONFIRM_TEXT}</code>
          </Label>
          <Input
            id="confirm-text"
            type="text"
            autoComplete="off"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            disabled={submitting}
            placeholder={CONFIRM_TEXT}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="password">Contraseña actual</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            placeholder="Reintroduce tu contraseña para autorizar"
          />
        </div>

        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}

        <div className="flex items-center gap-3 pt-1">
          <Button
            type="submit"
            variant="primary"
            disabled={!ready || submitting}
            className="bg-destructive hover:bg-destructive/90 text-white border-destructive"
          >
            {submitting ? 'Restaurando…' : 'Restaurar copia de seguridad'}
          </Button>
          {!ready && !submitting ? (
            <p className="text-[12px] text-muted-foreground">
              Sube un ZIP, escribe «{CONFIRM_TEXT}» y reintroduce tu contraseña para habilitar el botón.
            </p>
          ) : null}
        </div>
      </form>
    </Panel>
  );
}

function Panel({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-card border border-border rounded-sm shadow-sm overflow-hidden">
      <header className="px-6 py-4 border-b border-border">
        <h2 className="text-base font-semibold tracking-tightish">{title}</h2>
        {description ? (
          <p className="text-[13px] text-muted-foreground mt-0.5">{description}</p>
        ) : null}
      </header>
      {children}
    </section>
  );
}
